import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CtkViewer from "../../common/CtkViewer";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";
import { previewTemplateApi, errorMessage } from "./api";
import { CHANNEL_LABELS, channelsOf, usedVariables } from "./variables";
import VariableInputs from "./VariableInputs";

/**
 * Shows the finished SMS / WhatsApp / Email of a template. template: a saved template (with templateid) or,
 * with unsaved=true, the fields being edited. Optional Candidate ID + Course ID fill the database values;
 * the values typed when sending and upload columns can be typed in (by label).
 */
export default function PreviewDialog({ template, unsaved = false, systemVariables = [], onClose }) {
  const channels = channelsOf(template);
  const [candidateid, setCandidateid] = useState("");
  const [courseid, setCourseid] = useState("");
  const [values, setValues] = useState({});
  const [filevalues, setFilevalues] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(channels[0] || "sms");
  const [emailView, setEmailView] = useState("html");
  const [busy, setBusy] = useState(false);

  const used = usedVariables(template, undefined, systemVariables);
  const byLabel = (list) => list.map((v) => ({ ...v, name: v.label }));
  const sendVars = byLabel(used.filter((v) => v.source === "send"));
  const fileVars = byLabel(used.filter((v) => v.source === "file"));

  const run = async () => {
    setBusy(true);
    setError("");
    const body = {
      ...(unsaved ? { ...template } : { templateid: template.templateid }),
      values,
      filevalues,
      ...(candidateid || courseid ? { candidateid: Number(candidateid), courseid: Number(courseid) } : {}),
    };
    try {
      setResult((await previewTemplateApi(body)).data);
    } catch (err) {
      setResult(null);
      setError(errorMessage(err, "Could not preview"));
    }
    setBusy(false);
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line
  }, []);

  const idsInvalid = (candidateid || courseid) && (!/^\d+$/.test(candidateid) || !/^\d+$/.test(courseid));
  const r = result?.[tab];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preview — {template.eventname}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: "#555" }}>
              Enter a Candidate ID and Course ID to fill the values from the database, and any values typed when sending.
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField size="small" fullWidth label="Candidate ID" value={candidateid} onChange={(e) => setCandidateid(e.target.value.trim())} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField size="small" fullWidth label="Course ID" value={courseid} onChange={(e) => setCourseid(e.target.value.trim())} />
          </Grid>
          <Grid item xs={12}>
            <VariableInputs title={sendVars.length ? "Values typed when sending" : ""} variables={sendVars} values={values} onChange={(n, v) => setValues({ ...values, [n]: v })} />
          </Grid>
          <Grid item xs={12}>
            <VariableInputs title={fileVars.length ? "Values from the upload file" : ""} variables={fileVars} values={filevalues} onChange={(n, v) => setFilevalues({ ...filevalues, [n]: v })} />
          </Grid>
          <Grid item xs={12}>
            <BluePrimaryButton title={busy ? "Loading..." : "Refresh Preview"} handleSubmit={run} disabled={busy || !!idsInvalid} />
            {idsInvalid && <Typography variant="caption" sx={{ color: "red", ml: 2 }}>Give both Candidate ID and Course ID as numbers</Typography>}
          </Grid>
          {error && (
            <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>
          )}
          {result && (
            <Grid item xs={12}>
              <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
                {channels.map((c) => <Tab key={c} value={c} label={CHANNEL_LABELS[c]} />)}
              </Tabs>
              {r?.missing?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No value yet for {r.missing.map((m) => <Chip key={m} size="small" label={m} sx={{ mx: 0.5 }} />)} — these recipients would be skipped.
                </Alert>
              )}
              {r && tab === "email" && (
                <>
                  <Typography variant="caption" sx={{ color: "#666" }}>Subject</Typography>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>{r.subject}</Typography>
                  <Tabs value={emailView} onChange={(e, v) => setEmailView(v)} sx={{ minHeight: 32, mb: 1 }}>
                    <Tab value="html" label="Email" sx={{ minHeight: 32 }} />
                    <Tab value="text" label="Plain text" sx={{ minHeight: 32 }} />
                  </Tabs>
                  <Box sx={{ border: "1px solid #ccc", borderRadius: "4px", backgroundColor: "#f9f9f9", p: 2, minHeight: 200, overflowY: "auto" }}>
                    {emailView === "html" ? <CtkViewer content={r.html} /> : <Box component="pre" sx={{ whiteSpace: "pre-wrap", m: 0, fontFamily: "inherit" }}>{r.text}</Box>}
                  </Box>
                </>
              )}
              {r && tab !== "email" && (
                <>
                  <Box sx={{ border: "1px solid #ccc", borderRadius: "8px", backgroundColor: "#f9f9f9", p: 2, whiteSpace: "pre-wrap" }}>{r.text}</Box>
                  {tab === "sms" && <Typography variant="caption" sx={{ color: "#666" }}>{r.text.length} characters · DLT {template.sms?.dltid}</Typography>}
                  {tab === "whatsapp" && <Typography variant="caption" sx={{ color: "#666" }}>Template {template.whatsapp?.templatename} · values: {r.params.map((p, i) => `{{${i + 1}}} ${p || "—"}`).join(" | ")}</Typography>}
                </>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
